import { formatDurationHuman } from '@/utils'

import { hmsToMilliseconds, millisecondsToHms } from '~shared/timeutils'
import { CueRunState } from '~shared/utils'
import type { Timestamp } from '~shared/utils'

import { EditableHms } from './EditableHms'

type DurationPopoverContentProps = {
  duration?: number
  setDuration: (duration: number) => void
  disabled?: boolean
  cueState?: Timestamp['state'] | null
  originalDuration?: number
  actualDuration?: number
}

export function DurationPopoverContent({
  duration = 0,
  setDuration,
  disabled = false,
  cueState = null,
  originalDuration,
  actualDuration,
}: DurationPopoverContentProps) {
  const hms = millisecondsToHms(Math.ceil(duration / 1000) * 1000)
  const onHmsUpdate = ({
    hours,
    minutes,
    seconds,
  }: {
    hours: number
    minutes: number
    seconds: number
  }) => setDuration(hmsToMilliseconds({ hours, minutes, seconds }))

  // Generate description text based on provided props
  const formattedDuration = formatDuration({
    cueState,
    duration,
    originalDuration,
    actualDuration,
  })

  return (
    <div className="w-54 space-y-2">
      <h3 className="font-semibold text-sm">Cue Duration</h3>
      <EditableHms
        disabled={disabled}
        hms={hms}
        onUpdate={onHmsUpdate}
      />
      <p className="text-xs text-white/80">{formattedDuration}</p>
    </div>
  )
}

/**
 * Gets a human-readable description of a cue's duration
 */
function formatDuration({
  cueState,
  duration,
  originalDuration,
  actualDuration,
}: {
  cueState?: Timestamp['state'] | null
  duration: number
  originalDuration?: number
  actualDuration?: number
}) {
  const durationString = formatDurationHuman(duration)

  switch (cueState) {
    case CueRunState.CUE_PAST: {
      const original = originalDuration ?? 0
      const actual = actualDuration ?? 0
      const originalDurationString = formatDurationHuman(original)
      if (original === actual) {
        return `Ran exactly ${originalDurationString}`
      }
      const difference = Math.abs(original - actual - 1000)
      const diffDurationString = formatDurationHuman(difference)
      return `Ran ${diffDurationString} ${original < actual ? 'over' : 'under'} the expected ${originalDurationString}.`
    }
    case CueRunState.CUE_ACTIVE:
    case CueRunState.CUE_NEXT:
    case CueRunState.CUE_FUTURE:
    default:
      return `Running for ${durationString}.`
  }
}
